import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  Grid,
  Chip,
  IconButton,
  TablePagination,
  TableSortLabel,
  TextField,
  InputAdornment,
  CircularProgress,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  Hotel as HotelIcon,
  Phone as PhoneIcon,
  Business as BusinessIcon,
  TrendingUp as TrendingUpIcon,
  Receipt as ReceiptIcon,
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  ArrowBack as ArrowBackIcon,
  CalendarMonth as CalendarIcon,
} from '@mui/icons-material';
import { getPrePaidBooking, getPostPaidBooking, getSpecificHotelBooking, updatePrepaidPaymentStatus, setBookingType } from '../api/adminPanelApi';

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0 }).format(amount || 0);

const getCurrentMonthDateRange = (monthStr) => {
  const now = new Date();
  const selectedDate = new Date(monthStr + '-01');
  const currentMonth = now.toISOString().slice(0, 7);
  if (monthStr === currentMonth) {
    return { start: `${monthStr}-01`, end: now.toISOString().slice(0, 10) };
  }
  if (monthStr < currentMonth) {
    const lastDay = new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 0).getDate();
    return { start: `${monthStr}-01`, end: `${monthStr}-${String(lastDay).padStart(2, '0')}` };
  }
  return { start: `${currentMonth}-01`, end: now.toISOString().slice(0, 10) };
};

const BOOKING_TYPE = { ALL: 'all', PREPAID: 'prepaid', POSTPAID: 'postpaid' };

const mapPerHotelRow = (h, index, source) => ({
  id: `${source}-${index}`,
  hotel_id: h.hotel_id,
  hotel_name: h.hotel_name,
  contact_number: h.phone || '',
  total_bookings: h.totalBookings,
  total_revenue: h.totalRevenue,
  commission: h.totalTTCommission,
  gst: h.totalGst,
  total_tabtrips: h.tabtripsTotal,
  _source: source,
});

const mergeHotelsById = (hotels) => {
  const byId = new Map();
  hotels.forEach((h) => {
    const id = h.hotel_id;
    if (!byId.has(id)) {
      byId.set(id, {
        id,
        hotel_id: id,
        hotel_name: h.hotel_name,
        contact_number: h.contact_number,
        total_bookings: 0,
        total_revenue: 0,
        commission: 0,
        gst: 0,
        total_tabtrips: 0,
        prepaid_bookings: 0,
        postpaid_bookings: 0,
      });
    }
    const row = byId.get(id);
    row.total_bookings += Number(h.total_bookings) || 0;
    row.total_revenue += Number(h.total_revenue) || 0;
    row.commission += Number(h.commission) || 0;
    row.gst += Number(h.gst) || 0;
    row.total_tabtrips += Number(h.total_tabtrips) || 0;
    if (h._source === 'prepaid') row.prepaid_bookings += Number(h.total_bookings) || 0;
    else row.postpaid_bookings += Number(h.total_bookings) || 0;
  });
  return Array.from(byId.values());
};

const sumTotalData = (a, b) => {
  const left = a && typeof a === 'object' ? a : {};
  const right = b && typeof b === 'object' ? b : {};
  return {
    total_bookings: (Number(left.totalBookings ?? left.total_bookings) || 0) + (Number(right.totalBookings ?? right.total_bookings) || 0),
    total_revenue: (Number(left.totalRevenue ?? left.total_revenue) || 0) + (Number(right.totalRevenue ?? right.total_revenue) || 0),
    commission: (Number(left.totalTTCommission ?? left.commission) || 0) + (Number(right.totalTTCommission ?? right.commission) || 0),
    gst: (Number(left.totalGst ?? left.gst) || 0) + (Number(right.totalGst ?? right.gst) || 0),
    total_tabtrips: (Number(left.tabtripsTotal ?? left.total_tabtrips) || 0) + (Number(right.tabtripsTotal ?? right.total_tabtrips) || 0),
  };
};

const normalizeTotalData = (data) => {
  if (!data || typeof data !== 'object') {
    return { total_bookings: 0, total_revenue: 0, commission: 0, gst: 0, total_tabtrips: 0 };
  }
  return {
    total_bookings: Number(data.totalBookings) || 0,
    total_revenue: Number(data.totalRevenue) || 0,
    commission: Number(data.totalTTCommission) || 0,
    gst: Number(data.totalGst) || 0,
    total_tabtrips: Number(data.tabtripsTotal) || 0,
  };
};

const compareHotels = (a, b, field, order) => {
  const dir = order === 'asc' ? 1 : -1;
  const getVal = (row) => {
    switch (field) {
      case 'hotel_name':
        return (row.hotel_name || '').toLowerCase();
      case 'hotel_id':
        return Number(row.hotel_id) || 0;
      case 'prepaid_bookings':
        return Number(row.prepaid_bookings) || 0;
      case 'postpaid_bookings':
        return Number(row.postpaid_bookings) || 0;
      case 'total_bookings':
        return Number(row.total_bookings) || 0;
      case 'total_revenue':
        return Number(row.total_revenue) || 0;
      case 'total_tabtrips':
        return Number(row.total_tabtrips) || 0;
      default:
        return 0;
    }
  };
  const av = getVal(a);
  const bv = getVal(b);
  if (av < bv) return -1 * dir;
  if (av > bv) return 1 * dir;
  return 0;
};

const BookingsPanel = () => {
  const navigate = useNavigate();
  const [bookingTypeFilter, setBookingTypeFilter] = useState(BOOKING_TYPE.ALL);
  const [prepaidData, setPrepaidData] = useState({ hotels: [], totalData: null, cancelledHotels: [], cancelledTotalData: null });
  const [postpaidData, setPostpaidData] = useState({ hotels: [], totalData: null, cancelledHotels: [], cancelledTotalData: null });
  const [loading, setLoading] = useState(true);
  const [showCancelled, setShowCancelled] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [sortField, setSortField] = useState('total_revenue');
  const [sortOrder, setSortOrder] = useState('desc');
  const [mainSelectedMonth, setMainSelectedMonth] = useState('');
  const [mainStartDate, setMainStartDate] = useState('');
  const [mainEndDate, setMainEndDate] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    const now = new Date();
    const currentMonth = now.toISOString().slice(0, 7);
    const currentDate = now.toISOString().slice(0, 10);
    const firstDay = `${currentMonth}-01`;
    setMainSelectedMonth(currentMonth);
    setMainStartDate(firstDay);
    setMainEndDate(currentDate);
  }, []);

  const fetchBoth = async () => {
    setLoading(true);
    try {
      const [preRes, postRes] = await Promise.all([
        getPrePaidBooking(mainSelectedMonth, mainStartDate, mainEndDate),
        getPostPaidBooking(mainSelectedMonth, mainStartDate, mainEndDate),
      ]);
      if (preRes.success && preRes.data) {
        const perHotel = preRes.data.perHotelData || [];
        const cancelledPerHotel = preRes.data.cancelledPerHotelData || [];
        setPrepaidData({
          hotels: perHotel.map((h, i) => mapPerHotelRow(h, i, 'prepaid')),
          totalData: preRes.data.totalData,
          cancelledHotels: cancelledPerHotel.map((h, i) => mapPerHotelRow(h, i, 'prepaid')),
          cancelledTotalData: preRes.data.cancelledTotalData || null,
        });
      } else setPrepaidData({ hotels: [], totalData: null, cancelledHotels: [], cancelledTotalData: null });
      if (postRes.success && postRes.data) {
        const perHotel = postRes.data.perHotelData || [];
        const cancelledPerHotel = postRes.data.cancelledPerHotelData || [];
        setPostpaidData({
          hotels: perHotel.map((h, i) => mapPerHotelRow(h, i, 'postpaid')),
          totalData: postRes.data.totalData,
          cancelledHotels: cancelledPerHotel.map((h, i) => mapPerHotelRow(h, i, 'postpaid')),
          cancelledTotalData: postRes.data.cancelledTotalData || null,
        });
      } else setPostpaidData({ hotels: [], totalData: null, cancelledHotels: [], cancelledTotalData: null });
    } catch (e) {
      setSnackbar({ open: true, message: e.message || 'Failed to fetch data', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (mainSelectedMonth && mainStartDate && mainEndDate) fetchBoth();
  }, [mainSelectedMonth, mainStartDate, mainEndDate]);

  const mergedHotels = bookingTypeFilter === BOOKING_TYPE.ALL
    ? mergeHotelsById([...(prepaidData.hotels || []), ...(postpaidData.hotels || [])])
    : bookingTypeFilter === BOOKING_TYPE.PREPAID
      ? prepaidData.hotels
      : postpaidData.hotels;

  const mergedCancelledHotels = bookingTypeFilter === BOOKING_TYPE.ALL
    ? mergeHotelsById([...(prepaidData.cancelledHotels || []), ...(postpaidData.cancelledHotels || [])])
    : bookingTypeFilter === BOOKING_TYPE.PREPAID
      ? prepaidData.cancelledHotels
      : postpaidData.cancelledHotels;

  const totals = bookingTypeFilter === BOOKING_TYPE.ALL
    ? sumTotalData(prepaidData.totalData, postpaidData.totalData)
    : bookingTypeFilter === BOOKING_TYPE.PREPAID
      ? normalizeTotalData(prepaidData.totalData)
      : normalizeTotalData(postpaidData.totalData);

  const cancelledTotals = bookingTypeFilter === BOOKING_TYPE.ALL
    ? sumTotalData(prepaidData.cancelledTotalData, postpaidData.cancelledTotalData)
    : bookingTypeFilter === BOOKING_TYPE.PREPAID
      ? normalizeTotalData(prepaidData.cancelledTotalData)
      : normalizeTotalData(postpaidData.cancelledTotalData);

  const displayHotels = showCancelled ? mergedCancelledHotels : mergedHotels;

  const filteredHotels = displayHotels.filter(
    (h) =>
      !searchTerm ||
      (h.hotel_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      String(h.hotel_id || '').includes(searchTerm) ||
      (h.contact_number || '').includes(searchTerm)
  );

  const sortedHotels = useMemo(() => {
    const rows = [...filteredHotels];
    rows.sort((a, b) => compareHotels(a, b, sortField, sortOrder));
    return rows;
  }, [filteredHotels, sortField, sortOrder]);

  const paginatedHotels = useMemo(() => {
    const start = page * rowsPerPage;
    return sortedHotels.slice(start, start + rowsPerPage);
  }, [sortedHotels, page, rowsPerPage]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortOrder(field === 'hotel_name' ? 'asc' : 'desc');
    }
    setPage(0);
  };

  const sortProps = (field) => ({
    active: sortField === field,
    direction: sortField === field ? sortOrder : 'asc',
    onClick: () => handleSort(field),
  });

  const handleFilterChange = (e, val) => {
    if (val != null) {
      setBookingTypeFilter(val);
      setPage(0);
    }
  };

  const handleCancelledToggle = () => {
    setShowCancelled((prev) => !prev);
    setPage(0);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setPage(0);
  };

  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const handleMainMonthChange = (e) => {
    const newMonth = e.target.value;
    const now = new Date();
    const currentMonth = now.toISOString().slice(0, 7);
    if (newMonth > currentMonth) {
      setSnackbar({ open: true, message: 'Cannot select future months', severity: 'warning' });
      return;
    }
    setMainSelectedMonth(newMonth);
    const range = getCurrentMonthDateRange(newMonth);
    setMainStartDate(range.start);
    setMainEndDate(range.end);
  };

  const handleMainDateChange = (field, value) => {
    if (field === 'start') setMainStartDate(value);
    else setMainEndDate(value);
  };

  const handleApplyDateFilter = () => fetchBoth();

  const handleHotelClick = (hotel) => {
    const type = bookingTypeFilter === BOOKING_TYPE.ALL ? BOOKING_TYPE.PREPAID : bookingTypeFilter;
    navigate(type === BOOKING_TYPE.PREPAID ? '/prepaid' : '/postpaid', {
      state: { selectedHotelId: hotel.hotel_id, month: mainSelectedMonth, start: mainStartDate, end: mainEndDate },
    });
  };

  if (loading && mergedHotels.length === 0 && mergedCancelledHotels.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400, flexDirection: 'column', gap: 2 }}>
        <CircularProgress size={60} sx={{ color: '#FF7700' }} />
        <Typography variant="h6" color="text.secondary">Loading bookings...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <IconButton onClick={() => navigate('/')} sx={{ color: '#FF7700', '&:hover': { backgroundColor: 'rgba(255,119,0,0.1)' } }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" fontWeight={700} sx={{ color: '#2E4057' }}>Bookings</Typography>
        </Box>
        <Typography variant="body1" color="text.secondary">Total sales and bookings. Filter by Prepaid or Postpaid.</Typography>
      </Box>

      {/* Date filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight={600} sx={{ color: '#2E4057', mb: 2 }}>Date filters</Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Month</InputLabel>
              <Select value={mainSelectedMonth} label="Month" onChange={handleMainMonthChange}>
                {Array.from({ length: 12 }, (_, i) => {
                  const d = new Date();
                  d.setMonth(d.getMonth() - i);
                  const v = d.toISOString().slice(0, 7);
                  const label = d.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
                  return <MenuItem key={v} value={v}>{label}</MenuItem>;
                })}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField fullWidth size="small" label="Start" type="date" value={mainStartDate} onChange={(e) => handleMainDateChange('start', e.target.value)} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField fullWidth size="small" label="End" type="date" value={mainEndDate} onChange={(e) => handleMainDateChange('end', e.target.value)} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Button fullWidth variant="contained" onClick={handleApplyDateFilter} disabled={loading} sx={{ bgcolor: '#FF7700', '&:hover': { bgcolor: '#e65100' } }}>Apply</Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Totals */}
      <Grid container spacing={2} alignItems="stretch" sx={{ mb: 2 }}>
        <Grid item xs={6} sm={4} md={2}>
          <Card sx={{ background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)', border: '1px solid #90caf9', height: '100%' }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="h4" fontWeight={700} sx={{ color: '#1565c0', fontSize: '1.25rem' }}>{totals.total_bookings ?? 0}</Typography>
              <Typography variant="body2" sx={{ color: '#1976d2' }}>Total Bookings</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card sx={{ background: 'linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%)', border: '1px solid #ce93d8', height: '100%' }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="h4" fontWeight={700} sx={{ color: '#6a1b9a', fontSize: '1rem' }}>{formatCurrency(totals.total_revenue)}</Typography>
              <Typography variant="body2" sx={{ color: '#7b1fa2' }}>Total Revenue</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card sx={{ background: 'linear-gradient(135deg, #e8f5e8 0%, #c8e6c9 100%)', border: '1px solid #a5d6a7', height: '100%' }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="h4" fontWeight={700} sx={{ color: '#2e7d32', fontSize: '1rem' }}>{formatCurrency(totals.gst)}</Typography>
              <Typography variant="body2" sx={{ color: '#388e3c' }}>GST</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card sx={{ background: 'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)', border: '1px solid #ffcc80', height: '100%' }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="h4" fontWeight={700} sx={{ color: '#ef6c00', fontSize: '1rem' }}>{formatCurrency(totals.commission)}</Typography>
              <Typography variant="body2" sx={{ color: '#f57c00' }}>Commission</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card sx={{ background: 'linear-gradient(135deg, #fce4ec 0%, #f8bbd9 100%)', border: '1px solid #f48fb1', height: '100%' }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="h4" fontWeight={700} sx={{ color: '#ad1457', fontSize: '1rem' }}>{formatCurrency(totals.total_tabtrips)}</Typography>
              <Typography variant="body2" sx={{ color: '#c2185b' }}>TabTrips Total</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card sx={{ background: 'linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%)', border: '1px solid #ef9a9a', height: '100%' }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="h4" fontWeight={700} sx={{ color: '#c62828', fontSize: '1rem' }}>{formatCurrency(cancelledTotals.total_revenue)}</Typography>
              <Typography variant="body2" sx={{ color: '#d32f2f' }}>Cancellation</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search */}
      <TextField
        fullWidth
        placeholder="Search hotels by name, ID, or contact..."
        value={searchTerm}
        onChange={handleSearchChange}
        size="small"
        sx={{ mb: 2 }}
        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
      />

      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>Type:</Typography>
        <ToggleButtonGroup value={bookingTypeFilter} exclusive onChange={handleFilterChange} size="small" sx={{ height: 32 }}>
          <ToggleButton value={BOOKING_TYPE.ALL} sx={{ px: 1.5, py: 0.5, fontSize: '0.8125rem' }}>All</ToggleButton>
          <ToggleButton value={BOOKING_TYPE.PREPAID} sx={{ px: 1.5, py: 0.5, fontSize: '0.8125rem' }}>Prepaid</ToggleButton>
          <ToggleButton value={BOOKING_TYPE.POSTPAID} sx={{ px: 1.5, py: 0.5, fontSize: '0.8125rem' }}>Postpaid</ToggleButton>
        </ToggleButtonGroup>
        <ToggleButton
          selected={showCancelled}
          onChange={handleCancelledToggle}
          size="small"
          value="cancelled"
          sx={{
            height: 32,
            px: 1.5,
            fontSize: '0.8125rem',
            border: '1px solid rgba(0,0,0,0.12)',
            color: showCancelled ? '#fff' : '#d32f2f',
            backgroundColor: showCancelled ? '#d32f2f' : 'transparent',
            '&.Mui-selected': {
              color: '#fff',
              backgroundColor: '#d32f2f',
              '&:hover': { backgroundColor: '#b71c1c' },
            },
            '&:hover': { backgroundColor: showCancelled ? '#b71c1c' : 'rgba(211, 47, 47, 0.04)' },
          }}
        >
          Cancelled
        </ToggleButton>
      </Box>

      {/* Hotels table */}
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <Box sx={{ p: 2, borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="h6" fontWeight={600} sx={{ color: '#2E4057' }}>
            Hotels{showCancelled ? ` (${sortedHotels.length})` : ''}
          </Typography>
          <FormControl size="small" sx={{ minWidth: 110 }}>
            <InputLabel shrink>Rows</InputLabel>
            <Select value={rowsPerPage} label="Rows" onChange={handleRowsPerPageChange}>
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={25}>25</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={100}>100</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }}>
                  <TableSortLabel {...sortProps('hotel_name')}>Hotel</TableSortLabel>
                </TableCell>
                {bookingTypeFilter === BOOKING_TYPE.ALL && (
                  <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }} align="center">
                    <TableSortLabel {...sortProps('prepaid_bookings')} sx={{ flexDirection: 'row-reverse', '& .MuiTableSortLabel-icon': { marginRight: '4px', marginLeft: 0 } }}>
                      Prepaid
                    </TableSortLabel>
                  </TableCell>
                )}
                {bookingTypeFilter === BOOKING_TYPE.ALL && (
                  <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }} align="center">
                    <TableSortLabel {...sortProps('postpaid_bookings')} sx={{ flexDirection: 'row-reverse', '& .MuiTableSortLabel-icon': { marginRight: '4px', marginLeft: 0 } }}>
                      Postpaid
                    </TableSortLabel>
                  </TableCell>
                )}
                <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }} align="center">
                  <TableSortLabel {...sortProps('total_bookings')} sx={{ flexDirection: 'row-reverse', '& .MuiTableSortLabel-icon': { marginRight: '4px', marginLeft: 0 } }}>
                    Bookings
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }} align="right">
                  <TableSortLabel {...sortProps('total_revenue')} sx={{ flexDirection: 'row-reverse', '& .MuiTableSortLabel-icon': { marginRight: '4px', marginLeft: 0 } }}>
                    Revenue
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }} align="right">
                  <TableSortLabel {...sortProps('total_tabtrips')} sx={{ flexDirection: 'row-reverse', '& .MuiTableSortLabel-icon': { marginRight: '4px', marginLeft: 0 } }}>
                    TabTrips Total
                  </TableSortLabel>
                </TableCell>
                {!showCancelled && (
                  <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }} align="center">Actions</TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedHotels.length === 0 && (
                <TableRow>
                  <TableCell colSpan={showCancelled ? (bookingTypeFilter === BOOKING_TYPE.ALL ? 6 : 4) : (bookingTypeFilter === BOOKING_TYPE.ALL ? 7 : 5)} align="center" sx={{ py: 4 }}>
                    {showCancelled ? 'No cancelled bookings for this period.' : 'No hotels found.'}
                  </TableCell>
                </TableRow>
              )}
              {paginatedHotels.map((hotel) => (
                <TableRow
                  key={hotel.hotel_id || hotel.id}
                  hover
                  sx={{ cursor: showCancelled ? 'default' : 'pointer' }}
                  onClick={showCancelled ? undefined : () => handleHotelClick(hotel)}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <HotelIcon sx={{ color: showCancelled ? '#d32f2f' : '#FF7700', fontSize: 20 }} />
                      <Box>
                        <Typography variant="body2" fontWeight={500}>{hotel.hotel_name}</Typography>
                        <Typography variant="caption" color="text.secondary">ID: {hotel.hotel_id} · {hotel.contact_number || '–'}</Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  {bookingTypeFilter === BOOKING_TYPE.ALL && (
                    <TableCell align="center">
                      <Chip size="small" label={hotel.prepaid_bookings ?? hotel.total_bookings} sx={{ bgcolor: '#667eea', color: 'white' }} />
                    </TableCell>
                  )}
                  {bookingTypeFilter === BOOKING_TYPE.ALL && (
                    <TableCell align="center">
                      <Chip size="small" label={hotel.postpaid_bookings ?? 0} sx={{ bgcolor: '#f5576c', color: 'white' }} />
                    </TableCell>
                  )}
                  <TableCell align="center">
                    <Chip
                      size="small"
                      label={hotel.total_bookings}
                      sx={{ bgcolor: showCancelled ? '#d32f2f' : '#FF7700', color: 'white' }}
                    />
                  </TableCell>
                  <TableCell align="right">{formatCurrency(hotel.total_revenue)}</TableCell>
                  <TableCell align="right">{formatCurrency(hotel.total_tabtrips)}</TableCell>
                  {!showCancelled && (
                    <TableCell align="center">
                      <IconButton size="small" sx={{ color: '#FF7700' }} onClick={(e) => { e.stopPropagation(); handleHotelClick(hotel); }}>
                        <VisibilityIcon />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={sortedHotels.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(e, p) => setPage(p)}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        />
      </Paper>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default BookingsPanel;
